import { DateTime } from 'luxon';
import { isAbsent, isPresent, pick } from '../identity.ts';
import { isStandardDate, isStandardTime } from './standard.ts';
import { DefaultTimeZone } from './utils.ts';
export { DateTime } from 'luxon';

// This returns the DateTime-Local format which is used natively by <input> fields.
export function dateTimeLocal(date: DateTime) {
  return date.toFormat('yyyy-MM-dd hh:mm a');
}

export function dateTimeDebug(date: DateTime) {
  return isStandardTime(date)
    ? date.toFormat('HH:mm')
    : isStandardDate(date)
      ? date.toFormat('MM/dd/yy')
      : date.toFormat('MM/dd/yy HH:mm');
}

// 2020-01-02
// For Month+Day with no year, we will coerce to either 0000 (invalid) or 0001 (Gregorian).
export function toIso8601(date: DateTime | undefined, noYearFormat: 0 | 1 = 0) {
  if (isPresent(date) && date.year <= 1 && date.year !== noYearFormat) {
    date = DateTime.fromObject({ year: noYearFormat, month: date.month, day: date.day });
  }
  return date?.toISODate() ?? undefined;
}

// 2020-01-02T03:04:05.06Z
export function toRfc3339(date: DateTime<true>): string;
export function toRfc3339(date: DateTime | undefined): string | undefined;
export function toRfc3339(date: DateTime | undefined) {
  // https://github.com/moment/luxon/pull/1412
  // We're currently dropping milliseconds for Square. If we need them, parameterize.
  return date?.startOf('second').toISO({ suppressMilliseconds: true }) ?? undefined;
}

export const FriendlyDate: Intl.DateTimeFormatOptions = {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
};

export const FriendlyTime: Intl.DateTimeFormatOptions = {
  hour: 'numeric',
  minute: '2-digit',
};

export const FriendlyDateTime: Intl.DateTimeFormatOptions = { ...FriendlyDate, ...FriendlyTime };

export const SimpleDateTime: Intl.DateTimeFormatOptions = {
  month: 'numeric',
  day: 'numeric',
  year: '2-digit',
  hour: 'numeric',
  minute: '2-digit',
  timeZone: DefaultTimeZone,
};

export function humanDateTime(
  date: DateTime,
  options?: {
    format?: Intl.DateTimeFormatOptions;
    joiner?: ',' | 'at' | ' ';
    colloquial?: boolean;
  },
) {
  const format = options?.format ?? FriendlyDateTime;
  const joiner = options?.joiner ?? 'at';
  const colloquial = options?.colloquial ?? true;
  const result = [];

  const dateFormat = pick(
    format,
    'localeMatcher',
    'formatMatcher',
    'timeZone',
    'weekday',
    'era',
    'year',
    'month',
    'day',
  );
  if (colloquial && 'year' in dateFormat) {
    const thisYear = DateTime.now().year;
    const yearString = date.toLocaleString({ year: 'numeric' });
    let customYear = false;
    if (date.year === thisYear) {
      // Year is implicit.
      customYear = true;
    } else if (date.year === thisYear + 1) {
      result.push(`Next year (${yearString}) on`);
      customYear = true;
    } else if (date.year === thisYear - 1) {
      result.push(`Last year (${yearString}) on`);
      customYear = true;
    }
    if (customYear) {
      delete dateFormat.era;
      delete dateFormat.year;
    }
  }

  const timeFormat = pick(
    format,
    'localeMatcher',
    'formatMatcher',
    'timeZone',
    'hour',
    'minute',
    'second',
    'timeZoneName',
    'hour12',
  );
  result.push(date.toLocaleString(dateFormat));
  switch (joiner) {
    case ',':
      result[result.length - 1] = result[result.length - 1] + joiner;
      break;
    case ' ':
      break;
    default:
      result.push(joiner);
  }
  result.push(date.toLocaleString(timeFormat));

  return result.join(' ');
}

export type HumanTimeFormat = '2 minutes newer' | '2 minutes' | 'in 2 minutes' | 'for 2 minutes';

// https://dockyard.com/blog/2020/02/14/you-probably-don-t-need-moment-js-anymore
// TODO(bpm): There are a variety of relative time comparisons. Make an clean enum value for each.
// "2 hours older|newer", "2 hours before|after", "2 hours ago", "in 2 hours", "yesterday",
// "earlier|later today", "just now", etc.
export function humanTimeDifference(
  compare: DateTime,
  options?: {
    baseline?: DateTime | undefined;
    format?: HumanTimeFormat | undefined;
    minimumDiffInSeconds?: number | undefined;
  },
) {
  const baseline = options?.baseline ?? DateTime.now();
  // const relativeToNow = options?.baseline === undefined;
  const format = options?.format ?? 'in 2 minutes';
  // TODO: Think about this.
  const isBeforeBaseline = true as boolean; // compare < baseline;
  const factor = -1; // !isBeforeBaseline ? -1 : 1;
  const later = isBeforeBaseline ? baseline : compare;
  const earlier = isBeforeBaseline ? compare : baseline;

  const rtf = new Intl.RelativeTimeFormat('en', {
    numeric: 'always',
    style: 'long',
  });
  let parts: Intl.RelativeTimeFormatPart[];
  const diffInSeconds = (later.toMillis() - earlier.toMillis()) / 1000;
  const absDiffInSeconds = Math.abs(diffInSeconds);
  if (absDiffInSeconds < (options?.minimumDiffInSeconds ?? Number.MAX_VALUE)) {
    // TODO: 'now' with proper alternatives.
    return '';
  }
  // if (isSameDay(later, earlier)) {
  //   result = rtf.formatToParts(0, 'day');
  if (absDiffInSeconds < 60) {
    parts = rtf.formatToParts(Math.round(factor * diffInSeconds), 'second');
  } else if (absDiffInSeconds !== 3600 && absDiffInSeconds < 2 * 3600) {
    parts = rtf.formatToParts(Math.round((factor * diffInSeconds) / 60), 'minute');
  } else if (absDiffInSeconds < 24 * 3600) {
    parts = rtf.formatToParts(Math.round((factor * diffInSeconds) / 3600), 'hour');
  } else if (absDiffInSeconds < 7 * 24 * 3600) {
    parts = rtf.formatToParts(Math.round((factor * diffInSeconds) / (24 * 3600)), 'day');
  } else if (absDiffInSeconds < 8 * 7 * 24 * 3600) {
    parts = rtf.formatToParts(Math.round((factor * diffInSeconds) / (7 * 24 * 3600)), 'week');
  } else if (absDiffInSeconds < 52 * 7 * 24 * 3600) {
    parts = rtf.formatToParts(Math.round((factor * diffInSeconds) / (4 * 7 * 24 * 3600)), 'month');
  } else {
    parts = rtf.formatToParts(Math.round((factor * diffInSeconds) / (365 * 24 * 3600)), 'year');
  }
  return parts
    .map((p) => {
      if (p.type !== 'literal') {
        return p.value;
      }
      if (format === 'in 2 minutes') {
        return p.value;
      }
      if (format === 'for 2 minutes') {
        return p.value.replace('in ', 'for ');
      }
      if (format === '2 minutes') {
        return p.value.replace('in ', '');
      }
      const needle = 'ago';
      const index = p.value.indexOf(` ${needle}`); // Note space prefix for word break.
      if (index >= 0) {
        const replacement = isBeforeBaseline ? 'older' : 'newer';
        return (
          p.value.substring(0, index + 1) +
          replacement +
          p.value.substring(index + 1 + needle.length)
        );
      } else {
        return p.value;
      }
    })
    .join('');
}

export function humanInterval(
  start: DateTime,
  options?: {
    end?: DateTime | undefined;
    format?: Intl.DateTimeFormatOptions;
    joiner?: ',' | 'at' | ' ';
    colloquial?: boolean;
    durationFormat?: HumanTimeFormat | undefined;
    minimumDiffInSeconds?: number | undefined;
  },
) {
  if (isAbsent(options) || isAbsent(options.end)) {
    return humanDateTime(start, options);
  }
  let end = options.end;
  const format =
    options.durationFormat ?? ((options.joiner ?? 'at') === 'at' ? 'for 2 minutes' : '2 minutes');
  const minimumDiffInSeconds = options.minimumDiffInSeconds ?? 60;
  if (end < start) {
    [start, end] = [end, start];
  }
  const humanStart = humanDateTime(start, options);
  const humanDiff = humanTimeDifference(end, {
    baseline: start,
    format,
    minimumDiffInSeconds,
  });
  return humanDiff.length
    ? `${humanStart}${options.joiner === ',' ? ',' : ''} ${humanDiff}`
    : humanStart;
}
